import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const BrandContext = createContext(null);

const DEFAULT = {
  brand_name: "Adika Cipta Kreasindo",
  tagline: "Finance · Project",
  logo_url: null,
  logo_file_id: null,
  invoice_footer: null,
  primary_color: "#2D4739",
  sidebar_color: "#151A18",
  accent_color: "#D99C3B",
  invoice_font: "Outfit",
  bank_accounts: [],
};

// Simple color darken (mix towards black by 15%)
function darken(hex, factor = 0.85) {
  if (!hex || !hex.startsWith("#") || hex.length !== 7) return hex || "#22362B";
  const r = Math.max(0, Math.round(parseInt(hex.slice(1,3),16) * factor));
  const g = Math.max(0, Math.round(parseInt(hex.slice(3,5),16) * factor));
  const b = Math.max(0, Math.round(parseInt(hex.slice(5,7),16) * factor));
  return "#" + [r,g,b].map(n => n.toString(16).padStart(2,"0")).join("");
}

function applyBrand(brand) {
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", brand.primary_color);
  root.style.setProperty("--brand-primary-hover", darken(brand.primary_color));
  root.style.setProperty("--brand-sidebar", brand.sidebar_color);
  root.style.setProperty("--brand-accent", brand.accent_color);

  // Inject dynamic override style
  let el = document.getElementById("brand-override");
  if (!el) { el = document.createElement("style"); el.id = "brand-override"; document.head.appendChild(el); }
  const p = brand.primary_color, ph = darken(brand.primary_color), s = brand.sidebar_color, a = brand.accent_color;
  el.textContent = `
    [class~="bg-[#2D4739]"] { background-color: ${p} !important; }
    [class~="hover:bg-[#22362B]"]:hover { background-color: ${ph} !important; }
    [class~="bg-[#151A18]"], aside[class*="bg-[#151A18]"] { background-color: ${s} !important; }
    [class~="bg-[#D99C3B]"], [class*="bg-[#D99C3B]"] { background-color: ${a} !important; }
    [class~="border-l-[#D99C3B]"], [class*="border-l-[#D99C3B]"] { border-left-color: ${a} !important; }
    [class~="border-l-[#2D4739]"] { border-left-color: ${p} !important; }
    [class~="text-[#2D4739]"] { color: ${p} !important; }
    button[class*="bg-[#151A18]"] { background-color: ${s} !important; }
  `;
}

function loadFont(fontName) {
  if (!fontName || fontName === "Outfit") return; // already loaded in index.html
  const id = "brand-font-" + fontName.replace(/\s+/g,"-");
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id; link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g,"+")}:wght@400;500;700&display=swap`;
  document.head.appendChild(link);
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const token = localStorage.getItem("token");
      const logo_url = data.logo_file_id
        ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.logo_file_id}?auth=${token}`
        : null;
      const merged = {
        brand_name: data.brand_name || "Adika Cipta Kreasindo",
        tagline: data.tagline || "Finance · Project",
        logo_file_id: data.logo_file_id || null,
        logo_url,
        invoice_footer: data.invoice_footer || null,
        primary_color: data.primary_color || "#2D4739",
        sidebar_color: data.sidebar_color || "#151A18",
        accent_color: data.accent_color || "#D99C3B",
        invoice_font: data.invoice_font || "Outfit",
        bank_accounts: data.bank_accounts || [],
      };
      setBrand(merged);
      applyBrand(merged);
      loadFont(merged.invoice_font);
    } catch { setBrand(DEFAULT); applyBrand(DEFAULT); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return <BrandContext.Provider value={{ brand, reload: load }}>{children}</BrandContext.Provider>;
}

export const useBrand = () => useContext(BrandContext) || { brand: DEFAULT, reload: () => {} };
export { loadFont };
